import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import {
    Button,
    Form,
    Input,
    InputNumber,
    Layout,
    Modal,
    message,
    Space,
    Switch,
    Table,
} from 'antd';
import { FC, useEffect, useState } from 'react';

import { RegulatoryAssessmentAdminDto } from '@clh/api-client';
import { useApiClient } from '@clh/ui';

import { Content, Header } from '../layout';
import Spinner from '../spinner/spinner';

export const RegulatoryAssessments: FC = () => {
    const api = useApiClient();
    const [assessments, setAssessments] = useState<
        RegulatoryAssessmentAdminDto[]
    >([]);
    const [loading, setLoading] = useState(true);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingAssessment, setEditingAssessment] =
        useState<RegulatoryAssessmentAdminDto | null>(null);
    const [form] = Form.useForm();

    const fetchRegulatoryAssessments = async () => {
        if (!api) return;
        try {
            setLoading(true);
            const response =
                await api.regulatoryAssessmentsControllerGetRegulatoryAssessments(
                    {
                        includeInactive: true,
                    }
                );
            setAssessments(response.assessments);
        } catch (_error) {
            message.error('Failed to fetch regulatory assessments');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void fetchRegulatoryAssessments();
    }, [api]);

    const handleAddAssessment = () => {
        setEditingAssessment(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEditAssessment = (assessment: RegulatoryAssessmentAdminDto) => {
        setEditingAssessment(assessment);
        form.setFieldsValue({
            assessmentId: assessment.assessmentId,
            code: assessment.code,
            name: assessment.name,
            active: assessment.active ?? true,
        });
        setIsModalVisible(true);
    };

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();

            if (editingAssessment) {
                const updateDto: RegulatoryAssessmentAdminDto = {
                    assessmentId: values.assessmentId,
                    code: values.code,
                    name: values.name,
                    active: values.active,
                };

                await api?.regulatoryAssessmentsControllerUpdateRegulatoryAssessment(
                    {
                        regulatoryAssessmentAdminDto: updateDto,
                    }
                );
            } else {
                const createDto: RegulatoryAssessmentAdminDto = {
                    assessmentId: values.assessmentId,
                    code: values.code,
                    name: values.name,
                    active: values.active ?? true,
                };

                await api?.regulatoryAssessmentsControllerCreateRegulatoryAssessment(
                    {
                        regulatoryAssessmentAdminDto: createDto,
                    }
                );
            }

            message.success(
                `Regulatory Assessment ${
                    editingAssessment ? 'updated' : 'created'
                } successfully`
            );
            setIsModalVisible(false);
            void fetchRegulatoryAssessments();
        } catch (_error) {
            message.error('Failed to save regulatory assessment');
        }
    };

    const columns = [
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Assessment ID',
            dataIndex: 'assessmentId',
            key: 'assessmentId',
        },
        {
            title: 'Active',
            dataIndex: 'active',
            key: 'active',
            render: (active: boolean = true) => (
                <Switch checked={active} disabled />
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: RegulatoryAssessmentAdminDto) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEditAssessment(record)}
                    />
                </Space>
            ),
        },
    ];

    if (!api || loading) {
        return <Spinner />;
    }

    return (
        <Layout>
            <Header>
                <h2>Regulatory Assessments</h2>
            </Header>
            <Content>
                <div style={{ marginBottom: 16 }}>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAddAssessment}
                    >
                        Add Regulatory Assessment
                    </Button>
                </div>

                <Table
                    dataSource={assessments}
                    columns={columns}
                    rowKey="assessmentId"
                />

                <Modal
                    title={
                        editingAssessment
                            ? 'Edit Regulatory Assessment'
                            : 'Add Regulatory Assessment'
                    }
                    open={isModalVisible}
                    onOk={() => void handleModalOk()}
                    onCancel={() => setIsModalVisible(false)}
                    okText={editingAssessment ? 'Update' : 'Create'}
                >
                    <Form form={form} layout="vertical">
                        <Form.Item
                            name="code"
                            label="Code"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please enter a code',
                                },
                            ]}
                        >
                            <Input
                                disabled={!!editingAssessment}
                                placeholder="e.g. body_mechanics_ergonomics"
                            />
                        </Form.Item>
                        <Form.Item
                            name="name"
                            label="Name"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please enter a name',
                                },
                            ]}
                        >
                            <Input placeholder="e.g. Body Mechanics/Ergonomics" />
                        </Form.Item>
                        <Form.Item
                            name="assessmentId"
                            label="Assessment ID"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please enter an assessment ID',
                                },
                            ]}
                        >
                            <InputNumber
                                placeholder="e.g. 1205"
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="active"
                            label="Active"
                            valuePropName="checked"
                            initialValue={true}
                        >
                            <Switch />
                        </Form.Item>
                    </Form>
                </Modal>
            </Content>
        </Layout>
    );
};
